import { useEffect, useMemo, useState } from 'react'

import { CurrenciesData } from '@app/api'
import config from '@app/config'
import { User } from '@app/models'
import { getCookie } from '@app/services'

type UseRegionalSettingsOptions = {
  currencies: CurrenciesData
  currentUser: User
}

const getPreferredCurrency = (currentUser: User) => {
  const cookieCurrencyRaw = getCookie(config.variables.currency)
  const cookieCurrency = cookieCurrencyRaw
    ? decodeURIComponent(cookieCurrencyRaw)
      .replace(/(^"|"$)/g, '')
      .trim()
    : undefined

  return (
    cookieCurrency ||
    currentUser.regionalSettings?.currencyId ||
    currentUser.currencyId ||
    config.regionalSettings?.currencyId ||
    'USD'
  )
}

export const useRegionalSettings = ({
  currencies,
  currentUser,
}: UseRegionalSettingsOptions) => {
  const preferredCurrency = getPreferredCurrency(currentUser)
  const [selectedCurrencyValue, setSelectedCurrencyValue] =
    useState(preferredCurrency)

  useEffect(() => {
    setSelectedCurrencyValue(preferredCurrency)
  }, [preferredCurrency])

  const defaultCurrencyValue =
    currentUser.regionalSettings?.currencyId ||
    config.regionalSettings?.currencyId ||
    selectedCurrencyValue

  const currencyNames = useMemo(() => {
    const symbols = currencies.symbols || {}
    const localizations = currencies.localizations || {}
    const languageNames = localizations.en || {}

    return {
      names: languageNames,
      symbols,
    }
  }, [currencies.localizations, currencies.symbols])

  return {
    selectedCurrencyValue,
    defaultCurrencyValue,
    currencyNames,
  }
}
